/**
 * The peanut gallery.
 *
 * A feed of bots reacting to the game as it happens, in the voice of people who
 * have money on it. It is decoration and it knows it — nothing here touches a
 * rating, a record or a result, and the whole feature could be deleted without
 * the league noticing.
 *
 * The cast is fixed and lives here rather than being invented per game, because
 * the joke only works if it is the same handle. "I'm on Valenwood again" is
 * funny the fourth time somebody says it and means nothing the first. A big
 * roster with a few of them showing up each night gets both: recognition, and a
 * table that cannot predict who turns up.
 *
 * The other half of it is pacing, and that is what most of this file is about.
 * A batch of jokes that all land at once reads as a wall of text somebody
 * generated; the same jokes arriving one at a time over ten minutes read as a
 * room watching the game. So the model writes a batch and every post in it is
 * stamped with a time in the future — the feed is a schedule, and the client
 * shows what has come due.
 *
 * What the model writes is text to display and nothing else. It is never parsed
 * for meaning, never counted, and never allowed near the rating engine.
 */

/** One recurring account. */
export interface Persona {
  /** The @handle, without the @. Stable forever — it is the identity. */
  handle: string;
  /** What the feed shows above the handle. */
  name: string;
  /**
   * One line of voice, handed to the model as-is.
   *
   * Written as an instruction rather than a description, because a bio the
   * model has to interpret produces the same neutral voice for everybody.
   */
  voice: string;
  /**
   * How likely they are to turn up, roughly. Higher is more often.
   *
   * The point of the roster is that not everybody posts every night, so the
   * weights are gentle — nobody is a regular and nobody is a unicorn.
   */
  weight: number;
}

/**
 * The roster.
 *
 * Deliberately larger than any one game needs. Twenty-four accounts, with a
 * handful posting per beat, means a table sees a different corner of the
 * internet every night and still recognises most of it.
 */
export const CAST: readonly Persona[] = [
  {
    handle: 'mana_screwed',
    name: 'Danny “Mulligan” Reyes',
    voice: 'A degenerate gambler in total ruin. Bet sizing, bad beats, and money that was not his to lose. Everything he owns is on this game.',
    weight: 3,
  },
  {
    handle: 'rule_704',
    name: 'Actually, Rule 704',
    voice: 'A rules lawyer. Corrects things nobody said, cites rule numbers, is technically right about nothing relevant.',
    weight: 2,
  },
  {
    handle: 'doomposter',
    name: 'the format is dead',
    voice: 'A doom-poster. Every result is proof Commander is finished. Was already typing before the game started.',
    weight: 2,
  },
  {
    handle: 'lgs_owner_mike',
    name: 'Mike @ Dragon’s Hoard',
    voice: 'Owns a game store. Relates everything to inventory, singles prices and people who play too slowly in his shop.',
    weight: 2,
  },
  {
    handle: 'spikeeeee',
    name: 'SPIKE',
    voice: 'A competitive player. Contemptuous of every deck on the table. Types in short, brutal fragments.',
    weight: 2,
  },
  {
    handle: 'precon_andy',
    name: 'Precon Andy',
    voice: 'Defends preconstructed decks with his whole chest. Wholesome, slightly wounded, always optimistic.',
    weight: 2,
  },
  {
    handle: 'spec_god',
    name: 'SpecGod 📈',
    voice: 'A speculator. Announces he has bought out a card in response to anything that happens. Quotes invented prices.',
    weight: 2,
  },
  {
    handle: 'judge_calls',
    name: 'Judge, Please',
    voice: 'A judge. Neutral and unbothered to the point of menace. Rules on things that are not questions.',
    weight: 2,
  },
  {
    handle: 'cedh_dominic',
    name: 'Dominic (cEDH)',
    voice: 'A cEDH elitist. Every game shown to him is too slow. Suggests a turn-two win nobody asked about.',
    weight: 2,
  },
  {
    handle: 'gm_ser',
    name: 'gm ser 🌅',
    voice: 'Crypto-brained and heavily leveraged. Treats the league table like a token chart. Says gm, ser and wagmi unironically while being liquidated.',
    weight: 2,
  },
  {
    handle: 'reply_guy_47',
    name: 'reply guy',
    voice: 'Replies to posts nobody can see with agreement nobody asked for. Never has an original thought.',
    weight: 2,
  },
  {
    handle: 'brier_score',
    name: 'brier score enjoyer',
    voice: 'A statistics pedant. Quotes calibration, sample size and base rates at people having fun.',
    weight: 2,
  },
  {
    handle: 'foil_collector',
    name: 'Serialised 001/500',
    voice: 'Only cares whether cards are foil, misprinted or serialised. Indifferent to the actual game.',
    weight: 2,
  },
  {
    handle: 'clearly_at_work',
    name: 'in a meeting rn',
    voice: 'Watching from a job he is about to lose. Types furtively. Every post interrupted by someone walking past his desk.',
    weight: 2,
  },
  {
    handle: 'wizards_did_this',
    name: 'WOTC KNEW',
    voice: 'A conspiracy theorist. Every outcome was planned months ago by someone. Names no one specifically.',
    weight: 2,
  },
  {
    handle: 'timmy_forever',
    name: 'BIG CREATURE ENJOYER',
    voice: 'Only excited by large creatures and big numbers. Caps lock. Genuinely delighted by everything.',
    weight: 2,
  },
  {
    handle: 'the_archivist',
    name: 'The Archivist 📚',
    voice: 'Cites obscure historical precedent for everything, most of it invented, delivered with total confidence.',
    weight: 1,
  },
  {
    handle: 'salt_lord',
    name: 'saltlord',
    voice: 'Bitter former player. Every game reminds him of a grudge from years ago that he will not explain.',
    weight: 2,
  },
  {
    handle: 'moms_account',
    name: 'Linda (Ethan’s mum)',
    voice: 'Somebody’s mother who followed the wrong account. Supportive, confused, uses punctuation strangely.',
    weight: 2,
  },
  {
    handle: 'definitely_human',
    name: 'definitely a human 🤖',
    voice: 'An obvious bot pretending badly to be a person. Slightly wrong idioms. Unsettlingly enthusiastic.',
    weight: 2,
  },
  {
    handle: 'prepper_pat',
    name: 'Pat // off-grid',
    voice: 'A prepper who has bet the emergency fund. Reads every board state as a survival scenario. Mentions supplies, and what he has had to sell.',
    weight: 1,
  },
  {
    handle: 'hype_man',
    name: 'HYPE ACCOUNT 🔊',
    voice: 'A hype account with the voice of a UFC pay-per-view announcer. Ludicrous overstatement. Treats a kitchen table as a title fight.',
    weight: 2,
  },
  {
    handle: 'wrong_game',
    name: 'thought this was Yugioh',
    voice: 'Confidently commentates as though this were a different franchise entirely — UFC, League of Legends, Palworld, Predecessor, Lord of the Rings. Never notices.',
    weight: 1,
  },
  /*
   * The parody desk.
   *
   * Real public figures, played as parody accounts and labelled as such - the
   * joke is a recognisable speaking style aimed at a kitchen-table game of
   * Magic, which is exactly the shape of every political parody account that
   * has ever existed.
   *
   * Weighted low on purpose. One of these turning up is funny; a feed of them
   * is a different app. And the brief in `commentary.ts` on the server keeps
   * them to the game: the ruined-marriage material belongs to the invented
   * accounts, not to people who exist.
   */
  {
    handle: 'not_the_donald',
    name: 'Donald Trump (parody)',
    voice: 'Superlatives, asides, nicknames for the decks, and a claim that he predicted this. Sentence fragments. Tremendous.',
    weight: 1,
  },
  {
    handle: 'dark_brandon_46',
    name: 'Joe Biden (parody)',
    voice: 'Folksy, wandering, an anecdote that goes nowhere, then a sudden extremely specific threat about the board state. Come on, man.',
    weight: 1,
  },
  {
    handle: 'madam_vp_parody',
    name: 'Kamala Harris (parody)',
    voice: 'Restates the board state as though it were profound, in nested clauses, and laughs at her own point. Fond of "the significance of".',
    weight: 1,
  },
  {
    handle: 'no_drama_obama',
    name: 'Barack Obama (parody)',
    voice: 'Measured, rhetorical pauses, "let me be clear", a long build to a mild observation about mana curves.',
    weight: 1,
  },
  {
    handle: 'bernie_asks_once',
    name: 'Bernie Sanders (parody)',
    voice: 'Furious about the distribution of lands. Frames every board state as the top one per cent of permanents. Gesticulating in text.',
    weight: 1,
  },
  {
    handle: 'cruz_control_parody',
    name: 'Ted Cruz (parody)',
    voice: 'Lawyerly and faintly wounded. Relitigates a ruling nobody made. Announces a podcast episode about the game.',
    weight: 1,
  },
  {
    handle: 'one_card_guy',
    name: 'have you tried Rhystic Study',
    voice: 'Recommends the same single card as the answer to every situation, no matter how irrelevant.',
    weight: 2,
  },
];

export const personaOf = (handle: string): Persona | undefined =>
  CAST.find((p) => p.handle === handle);

/** One post in the feed. */
export interface CommentaryPost {
  id: string;
  handle: string;
  name: string;
  text: string;
  /**
   * When it lands — usually in the future when it is written.
   *
   * The whole pacing model in one field. The server decides when each post is
   * due and every phone shows it at the same moment without anything having to
   * be pushed.
   */
  at: number;
  /** The beat that produced it — see `beatKey`. */
  beat: string;
  /**
   * Fake engagement.
   *
   * A timeline without numbers under the posts does not read as a timeline.
   * Invented on the server so every phone shows the same figures, rather than
   * each one rolling its own and the table comparing screens.
   */
  likes: number;
  reposts: number;
}

/** Commentary as it is stored on the live game. */
export interface Commentary {
  posts: CommentaryPost[];
  /** Beats already written, so a second phone asking cannot double-post. */
  beats: string[];
  /**
   * A batch being written right now: which handles, and since when.
   *
   * Only covers the gap between asking the model and the model answering.
   * Once posts exist, the typing row comes from the schedule instead — see
   * `typingNow`. Ignored once stale, so a Lambda that dies mid-generation does
   * not leave the feed typing forever.
   */
  typing?: { handles: string[]; since: number; beat: string };
}

/** How long a typing indicator is believed before the feed gives up on it. */
export const TYPING_TTL_MS = 45_000;

/** How long before a post is due that its author starts typing. */
export const TYPING_LEAD_MS = 6_000;

/**
 * How long a post is held over the table before it fades.
 *
 * The feed is a sheet somebody has to open, which is the wrong shape for a game
 * being played: nobody puts the counter down to read a timeline. So a post also
 * gets a moment on the table as it lands and then gets out of the way.
 *
 * Twenty seconds, up from eight. Eight was long enough to read twenty-five
 * words if you happened to be looking when they arrived, and at a table nobody
 * is - somebody is shuffling, somebody is reading a card, and the post was gone
 * before anyone looked up. It can afford to be generous now that it carries a
 * countdown and a button to end it early.
 */
export const FLASH_MS = 20_000;

/** What the feed is reacting to. */
export type Beat =
  | { kind: 'pre' }
  | { kind: 'out'; pilotId: string }
  | { kind: 'low'; pilotId: string }
  | { kind: 'post' }
  /** Nothing happened for a while and the room got chatty. */
  | { kind: 'idle'; n: number };

/**
 * A beat's stable name.
 *
 * The dedupe key. Two phones noticing the same elimination at the same moment
 * produce the same string, and the second write is refused rather than posting
 * a second round of jokes about it.
 */
export function beatKey(beat: Beat): string {
  switch (beat.kind) {
    case 'pre':
      return 'pre';
    case 'post':
      return 'post';
    case 'idle':
      return `idle:${beat.n}`;
    default:
      return `${beat.kind}:${beat.pilotId}`;
  }
}

/** Life at which the feed starts writing somebody's obituary early. */
export const LOW_LIFE = 10;

/** A burst — the table sitting down, or the result landing. */
const BURST = { min: 3, max: 6, spreadMs: 55_000 };
/** Somebody went out, or is about to. Fewer posts, closer together. */
const EVENT = { min: 2, max: 3, spreadMs: 20_000 };
/** The quiet drip while a game is just being played. */
const IDLE_GAP = { min: 60_000, max: 240_000 };

/** How many posts a beat is worth, and how long they take to arrive. */
export function shapeOf(beat: Beat): { min: number; max: number; spreadMs: number } {
  switch (beat.kind) {
    case 'pre':
    case 'post':
      return BURST;
    case 'out':
      return EVENT;
    case 'low':
      return { min: 1, max: 2, spreadMs: EVENT.spreadMs };
    case 'idle':
      // One voice at a time. The drip is the point; two people saying something
      // unprompted at the same moment is a burst, and bursts are for events.
      return { min: 1, max: 1, spreadMs: 0 };
  }
}

const between = (lo: number, hi: number, rng: () => number) =>
  lo + Math.floor(rng() * (hi - lo + 1));

/**
 * When each post in a batch is due.
 *
 * A burst starts almost immediately and finishes inside a minute; an event is
 * tighter than that; a lone idle post is pushed one to four minutes out, which
 * is what paces the whole quiet stretch of a game. Sorted, because a feed whose
 * posts arrive out of order reads as broken rather than as lively.
 */
export function scheduleFor(
  beat: Beat,
  count: number,
  now: number,
  rng: () => number = Math.random,
): number[] {
  if (beat.kind === 'idle') return [now + between(IDLE_GAP.min, IDLE_GAP.max, rng)];

  const { spreadMs } = shapeOf(beat);
  const at = Array.from({ length: count }, () => now + Math.floor(rng() * spreadMs));
  at.sort((a, b) => a - b);
  // The first one is prompt. Waiting twenty seconds for the room to react to
  // somebody dying reads as the feature being broken.
  at[0] = now + Math.floor(rng() * 2_000);
  return at;
}

export function countFor(beat: Beat, rng: () => number = Math.random): number {
  const { min, max } = shapeOf(beat);
  return between(min, max, rng);
}

/**
 * What the feed owes the table right now, or null if it is up to date.
 *
 * Deliberately one at a time. A pod that loses two players in a minute should
 * produce two rounds of posts in the order they happened, not one batch that
 * treats them as a single event.
 *
 * Order matters: the opening comes before anything else, the result comes after
 * everything, and idle chatter only fills a silence — if any post is still
 * waiting to land, the room is not silent and nobody needs prompting.
 */
export function nextBeat(
  game: { phase: string },
  seats: readonly {
    pilotId: string | null;
    deckId?: string | null;
    life: number;
    outAt: number | null;
  }[],
  commentary: Commentary | undefined,
  now = Date.now(),
): Beat | null {
  const already = new Set(commentary?.beats ?? []);
  const posts = commentary?.posts ?? [];

  /*
   * The opening lands in the lobby, not at kick-off.
   *
   * It fired on `startedAt` first, which put it a beat too late: the odds are
   * on screen in the lobby, the table is still arguing about them, and that is
   * the moment the bots exist to react to. By the time somebody presses Begin
   * the argument is over.
   *
   * Two seats with a deck each is the trigger, because that is the earliest
   * point there is anything to have an opinion about.
   */
  const seated = seats.filter((s) => s.pilotId && s.deckId).length;
  if (seated >= 2 && !already.has('pre')) return { kind: 'pre' };
  // Nothing else is worth saying before the opening has been said.
  if (!already.has('pre')) return null;

  if (game.phase === 'done') return already.has('post') ? null : { kind: 'post' };

  /*
   * Everything below is about a game actually in progress.
   *
   * Which matters most for the lobby: somebody opens a table, wanders off, and
   * it sits there for the twelve hours it takes to be swept. Without this the
   * room would fill that silence every couple of minutes, all night, at a
   * model call a time.
   */
  if (game.phase !== 'playing') return null;

  for (const seat of seats) {
    if (!seat.pilotId || seat.outAt === null) continue;
    const beat: Beat = { kind: 'out', pilotId: seat.pilotId };
    if (!already.has(beatKey(beat))) return beat;
  }

  for (const seat of seats) {
    if (!seat.pilotId || seat.outAt !== null || seat.life > LOW_LIFE) continue;
    const beat: Beat = { kind: 'low', pilotId: seat.pilotId };
    if (!already.has(beatKey(beat))) return beat;
  }

  // Everything written has already landed, so the room has gone quiet. The next
  // idle post schedules itself a minute or more out, which is what stops this
  // from firing again the moment it is answered.
  if (posts.some((p) => p.at > now)) return null;
  const n = [...already].filter((k) => k.startsWith('idle:')).length + 1;
  return { kind: 'idle', n };
}

/**
 * Who is posting this time.
 *
 * Weighted, without replacement, and never the same shape twice. The randomness
 * is the feature: a table that could predict the roster would stop reading it.
 */
export function castFor(count: number, rng: () => number = Math.random): Persona[] {
  const pool = [...CAST];
  const picked: Persona[] = [];
  while (picked.length < count && pool.length > 0) {
    const total = pool.reduce((sum, p) => sum + p.weight, 0);
    let roll = rng() * total;
    let index = pool.findIndex((p) => (roll -= p.weight) <= 0);
    if (index < 0) index = pool.length - 1;
    picked.push(pool[index]!);
    pool.splice(index, 1);
  }
  return picked;
}

/**
 * How many of them go completely off the rails.
 *
 * Most of them, most of the time. This started at roughly a third and the
 * batches came back competent — a page of mildly amusing observations, which is
 * worse than no feed at all. The thing worth reading is the post that has lost
 * contact with the game being played, so the default is now half the batch and
 * often four in five, and never fewer than one.
 */
export function unhingedCount(size: number, rng: () => number = Math.random): number {
  return Math.max(1, Math.round(size * (rng() < 0.4 ? 0.8 : 0.5)));
}

/** The posts that have actually landed, oldest first. */
export function feedOf(commentary: Commentary | undefined, now = Date.now()): CommentaryPost[] {
  return (commentary?.posts ?? []).filter((p) => p.at <= now).sort((a, b) => a.at - b.at);
}

/**
 * The post to hold over the table right now, if any.
 *
 * The newest one to have landed inside the flash window. Newest rather than
 * oldest-unseen, because this is a glance and not a queue - if two arrive close
 * together the second is the one worth showing, and both are in the feed for
 * anybody who wants them.
 */
export function flashOf(
  commentary: Commentary | undefined,
  now = Date.now(),
): CommentaryPost | null {
  let best: CommentaryPost | null = null;
  for (const p of commentary?.posts ?? []) {
    if (p.at > now || now - p.at > FLASH_MS) continue;
    if (!best || p.at > best.at) best = p;
  }
  return best;
}

/**
 * Who the table should see typing right now.
 *
 * Two sources, both real: a batch being written by the model, and a post that
 * is scheduled and nearly due. The second is what makes the quiet stretches
 * work — the room visibly starts composing a few seconds before it says
 * anything, which is the difference between a feed and a list.
 */
export function typingNow(commentary: Commentary | undefined, now = Date.now()): string[] {
  const typing = commentary?.typing;
  if (typing && now - typing.since <= TYPING_TTL_MS) return typing.handles;

  return (commentary?.posts ?? [])
    .filter((p) => p.at > now && p.at - now <= TYPING_LEAD_MS)
    .map((p) => p.handle);
}
